import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from "@angular/core";
import { FormGroup, Validators } from "@angular/forms";
import { SubSink } from "subsink";
import { DynamicFormEvent } from "../../../../dynamic-forms/dynamic-form-event.model";
import { SaveGroup } from "../../../../dynamic-forms/form-groups/save-group/save-group.model";
import { StandardGroup } from "../../../../dynamic-forms/form-groups/standard-group/standard-group.model";
import { FormService } from "../../../../dynamic-forms/form.service";
import { Dropdown } from "../../../../dynamic-forms/inputs/dropdown/dropdown.model";
import { DynamicInput } from "../../../../dynamic-forms/inputs/dynamic-input.model";
import { SelectableInput } from "../../../../dynamic-forms/inputs/selectable-input.model";
import { Textbox } from "../../../../dynamic-forms/inputs/textbox/textbox.model";
import { DynamicEntityAttribute } from "../../../../platform/api/member-validation/dynamic-entity-attribute.model";
import { DynamicEntity } from "../../../../platform/api/member-validation/dynamic-entity.model";
import { EntityType } from "../../../../platform/api/member-validation/entity-type.enum";
import { WorkflowStatusDb } from "../../../../platform/api/workflow/workflow-status-db.enum";
import { DATE_OF_BIRTH_CONFIRM, GENDER_CONFIRM } from "../../../../platform/modules/member/chase-detail/chase-detail-chart/attributes";
import { ChartService } from "../../../../platform/modules/member/chase-detail/chase-detail-chart/chart.service";
import { RiskHelper } from "../../../../platform/modules/member/chase-detail/chase-detail-chart/risk/risk-helper.model";
import { ChaseDetailState } from "../../../../platform/modules/member/chase-detail/chase-detail-state.model";
import { ChaseDetailStateService } from "../../../../platform/modules/member/chase-detail/chase-detail-state.service";
import { ArrayHelper } from "../../../../utilities/contracts/array-helper";
import { DateHelper } from "../../../../utilities/contracts/date-helper";
import { NumberHelper } from "../../../../utilities/contracts/number-helper";
import { StringHelper } from "../../../../utilities/contracts/string-helper";

@Component({
  selector: "member-dob-gen-v2",
  templateUrl: "./dob-gen-v2.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DobGenV2Component implements OnInit, OnDestroy {
  @Input() isChaseEnabled = false;
  private sink = new SubSink();
  private hasChaseId = false;
  enabled = false;
  chaseId: number;

  form: FormGroup;
  saveGroup: SaveGroup;
  standardGroup: StandardGroup;
  private memberFirstNameInput: Textbox;
  private memberLastNameInput: Textbox;
  private memberDobInput: Textbox;
  private memberGenderInput: Textbox;
  private confirmDobInput: Dropdown;
  private confirmGenderInput: Dropdown;

  constructor(
    private chaseDetailStateService: ChaseDetailStateService,
    private formService: FormService,
    private chartService: ChartService,
    private changeDetector: ChangeDetectorRef
  ) { }

  ngOnInit() {

    this.initializeForm();

    this.sink.add(
      this.chaseDetailStateService.state.subscribe(state => {
        if (!this.hasChaseId && NumberHelper.isGreaterThan(state.chaseId, 0)) {
          this.hasChaseId = true;
          this.chaseId = state.chaseId;
          this.chartService.getEntities(this.chaseId).subscribe(this.setEntities(this.chaseId).bind(this));
        }

        this.enabled = state.isEnabled;

        this.createMemberForm(state);
        if (ArrayHelper.isAvailable(state.formEnities)) {
          this.createForm(state);
        }

        this.changeDetector.markForCheck();
      })
    );
  }

  ngOnDestroy() {
    this.sink.unsubscribe();
  }

  handleChange(event: DynamicFormEvent): void {
    switch (event.type) {
      case "save":
        this.saveAttributesByInputs(event.model.controls);
        break;
      case "clear":
      case "delete":
        if (!this.formService.isKeyDownEnter(event.originalEvent)) {
          this.deleteAttributesByInputs(event.model.controls);
        }
        break;
      default:
      case "compliance":
        break;
    }
  }

  private saveAttributesByInputs(controls: DynamicInput[]): void {
    const attributes = controls
      .map(this.getSaveAttribute.bind(this))
      .filter(this.getUniqueAttributes.bind(this)) as DynamicEntityAttribute[];
    this.saveAttributes(attributes);
  }

  private deleteAttributesByInputs(controls: DynamicInput[]): void {
    const allControlsDisabled = controls.every(a => a.disabled);
    if (!allControlsDisabled) {
      const attributesToDelete = controls
        .map(this.getDeleteAttribute.bind(this))
        .filter(this.getUniqueAttributes.bind(this)) as DynamicEntityAttribute[];
      this.saveAttributes(attributesToDelete);
    }
  }

  private saveAttributes(attributes: DynamicEntityAttribute[]) {
    this.chartService
      .save2(attributes)
      .subscribe({
        next: this.setEntities(this.chaseId).bind(this) as any,
        error: (e: HttpErrorResponse) => this.form.setErrors({ saveError: e.error }),
      });
  }

  private setEntities(chaseId: number): any {
    return (entities: DynamicEntity[]): void => {
      const hasConfirmationEntity = entities.some(a => a.entityTypeId === EntityType.CONFIRMATION);
      if (!hasConfirmationEntity) {
        const defaultConfirmationEntity = new DynamicEntity({
          chaseId,
          entityTypeId: EntityType.CONFIRMATION,
        });
        entities.push(defaultConfirmationEntity);
      }
      this.chaseDetailStateService.setData({ entities });
    };
  }

  private initializeForm(): void {
    this.memberFirstNameInput = new Textbox({
      key: "MemberFirstName",
      label: "Member First Name",
      disabled: true,
    });

    this.memberLastNameInput = new Textbox({
      key: "MemberLastName",
      label: "Member Last Name",
      disabled: true,
    });

    this.memberDobInput = new Textbox({
      key: "MemberDateOfBirth",
      label: "Member Date Of Birth",
      disabled: true,
    });

    this.memberGenderInput = new Textbox({
      key: "MemberGender",
      label: "Member Gender",
      disabled: true,
    });

    this.confirmDobInput = new Dropdown({
      key: "ConfirmDob",
      label: "Confirm DOB?",
      placeholder: "Yes/No",
      validators: [Validators.required],
      errorMessages: {
        required: "Confirm DOB is required",
      },
      options: [
        new SelectableInput({ text: "Yes", value: "1" }),
        new SelectableInput({ text: "No", value: "0" }),
      ],
    });

    this.confirmGenderInput = new Dropdown({
      key: "ConfirmGender",
      label: "Confirm Gender?",
      placeholder: "Yes/No",
      validators: [Validators.required],
      errorMessages: {
        required: "Confirm Gender is required",
      },
      options: [
        new SelectableInput({ text: "Yes", value: "1" }),
        new SelectableInput({ text: "No", value: "0" }),
      ],
    });

    this.setSaveGroupAndCreateForm();
  }

  private setSaveGroupAndCreateForm(): void {
    this.saveGroup = new SaveGroup({
      key: "DobSave",
      controls: [
        this.confirmDobInput,
        this.confirmGenderInput,
      ],
    });
    this.confirmDobInput.parent = this.saveGroup;
    this.confirmGenderInput.parent = this.saveGroup;

    this.standardGroup = new StandardGroup({
      key: "MemberStandard",
      header: "Member Information",
      controls: [
        this.memberFirstNameInput,
        this.memberLastNameInput,
        this.memberDobInput,
        this.memberGenderInput,
      ],
    });
    this.memberFirstNameInput.parent = this.standardGroup;
    this.memberLastNameInput.parent = this.standardGroup;
    this.memberDobInput.parent = this.standardGroup;
    this.memberGenderInput.parent = this.standardGroup;

    this.form = this.formService.createFormGroup([
      this.standardGroup,
      this.saveGroup,
    ]);
  }

  private createForm(state: ChaseDetailState): void {
    const chaseId = state.chaseId;
    const workflowStatusName = this.getWorkflowStatusName(state.workflowStatus);
    const singleEntity = state.formEnities[0];
    const entityId = singleEntity.entityId;
    const entityTypeId = singleEntity.entityTypeId;
    const disableByMemberValidation = !this.enabled;

    const confirmDobAttribute = {
      ...RiskHelper.getAttribute(DATE_OF_BIRTH_CONFIRM.attributeCode, singleEntity),
      chaseId,
      entityId,
      entityTypeId,
      ...DATE_OF_BIRTH_CONFIRM,
    };
    this.confirmDobInput.saveInfo = confirmDobAttribute;
    this.confirmDobInput.value = confirmDobAttribute.value;
    this.confirmDobInput.disabled = disableByMemberValidation;
    this.confirmDobInput.isChanged = confirmDobAttribute.isChanged;
    this.confirmDobInput.workflowStatusName = workflowStatusName;
    const confirmDobControl = this.form.get(this.confirmDobInput.getMasterKey());
    confirmDobControl.setValue(confirmDobAttribute.value);
    disableByMemberValidation ? confirmDobControl.disable() : confirmDobControl.enable();

    const confirmGenderAttribute = {
      ...RiskHelper.getAttribute(GENDER_CONFIRM.attributeCode, singleEntity),
      chaseId,
      entityId,
      entityTypeId,
      ...GENDER_CONFIRM,
    };
    this.confirmGenderInput.saveInfo = confirmGenderAttribute;
    this.confirmGenderInput.value = confirmGenderAttribute.value;
    this.confirmGenderInput.disabled = disableByMemberValidation;
    this.confirmGenderInput.isChanged = confirmGenderAttribute.isChanged;
    this.confirmGenderInput.workflowStatusName = workflowStatusName;
    const confirmGenderControl = this.form.get(this.confirmGenderInput.getMasterKey());
    confirmGenderControl.setValue(confirmGenderAttribute.value);
    disableByMemberValidation ? confirmGenderControl.disable() : confirmGenderControl.enable();
  }

  private createMemberForm(state: ChaseDetailState): void {
    if (state != null && state.member != null) {
      this.form.get(this.memberFirstNameInput.getMasterKey()).setValue(state.member.memberFirstName);
      this.form.get(this.memberLastNameInput.getMasterKey()).setValue(state.member.memberLastName);
      this.form.get(this.memberDobInput.getMasterKey()).setValue(DateHelper.format(state.member.memberDateOfBirth));
      this.form.get(this.memberGenderInput.getMasterKey()).setValue(state.member.memberGender);
    }
  }

  private getWorkflowStatusName(workflowStatus: WorkflowStatusDb): string {
    return StringHelper.clean(WorkflowStatusDb[workflowStatus]).toLowerCase();
  }

  private getSaveAttribute(control: DynamicInput): DynamicEntityAttribute {
    const saveAttribute = (control as any).saveInfo as DynamicEntityAttribute;
    saveAttribute.value = this.form.get(control.getMasterKey()).value;
    return saveAttribute;
  }

  private getDeleteAttribute(control: DynamicInput): DynamicEntityAttribute {
    const deleteAttribute = this.getSaveAttribute(control);
    if (!deleteAttribute.isAdmin) {
      deleteAttribute.value = "";
      deleteAttribute.pageNumber = null;
      deleteAttribute.validationId = null;
      deleteAttribute.validationNote = "";
    }
    return deleteAttribute;
  }

  private getUniqueAttributes(attribute: DynamicEntityAttribute, index: number, attributes: DynamicEntityAttribute[]): boolean {
    return attributes.findIndex(a => a.attributeId === attribute.attributeId && a.isAdmin === attribute.isAdmin) === index;
  }
}
